import { useAppDispatch } from '../store';
import { Dispatch, SetStateAction, useState } from 'react';
import { ApiResponse, BaseRepository } from './commonClasses';
import { AxiosError } from 'axios';
import { ACT_SET, TYPE_ELEMENT } from './commonConstants';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IBaseModel, IListFetchParams } from './commonInterfaces';
import { MODULE, CrudID } from './commonTypes';
import { handleAxiosError } from '../utils';

interface Props<T extends IBaseModel, Q extends IListFetchParams> {
  moduleName: MODULE;
  repository: BaseRepository<T, Q>;
  id: CrudID;
  updateStore?: boolean;
}

interface Return<T extends IBaseModel> {
  fetchDataElement: () => void;
  methods: UseFormReturn<T>;
  loading: boolean;
  setloading: Dispatch<SetStateAction<boolean>>;
  dataElement?: T;
}

export const useCrud = <T extends IBaseModel, Q extends IListFetchParams>({
  moduleName,
  repository,
  updateStore = true,
  id,
}: Props<T, Q>): Return<T> => {
  const dispatch = useAppDispatch();
  const [loading, setloading] = useState<boolean>(false);
  const [dataElement, setDataElement] = useState<T>();

  const methods = useForm<T>({
    mode: 'onSubmit',
    values: dataElement,
    defaultValues: dataElement
      ? JSON.parse(
          JSON.stringify(dataElement, (key, value) =>
            value === null ? undefined : value,
          ),
        )
      : undefined,
    // @ts-ignore
    resolver: zodResolver(repository.formValidationSchema),
  });

  const fetchDataElement = async () => {
    setloading(true);
    id &&
      (await repository
        .get(id)
        .then((response: ApiResponse<T>) => {
          response?.data && setData(response?.data);
        })
        .catch((err: AxiosError) => handleAxiosError(err)));
    setloading(false);
  };

  const setData = async (data: T) => {
    updateStore &&
      (await dispatch({
        type: `${moduleName}/${TYPE_ELEMENT}/${ACT_SET}`,
        payload: data,
      }));
    setDataElement(data);
  };

  return {
    fetchDataElement,
    dataElement,
    methods,
    loading,
    setloading,
  };
};
